// Author: Ahsan Saleem
/**
 * Theme control. Two operating modes — Command (dark) and Field (light) — plus
 * "auto" which follows the OS. The choice is stamped as data-theme on the root
 * element so the token layer resolves the right palette.
 */
import { useEffect, useState } from 'react';

export type ThemeChoice = 'command' | 'field' | 'auto';
const KEY = 'bloomos_theme';

export function initTheme(): void {
  const saved = localStorage.getItem(KEY) as ThemeChoice | null;
  if (saved === 'command' || saved === 'field') {
    document.documentElement.setAttribute('data-theme', saved);
  }
}

export function getChoice(): ThemeChoice {
  return (localStorage.getItem(KEY) as ThemeChoice) ?? 'auto';
}

export function effectiveTheme(): 'command' | 'field' {
  const attr = document.documentElement.getAttribute('data-theme');
  if (attr === 'command' || attr === 'field') return attr;
  return matchMedia('(prefers-color-scheme: dark)').matches ? 'command' : 'field';
}

export function setTheme(choice: ThemeChoice): void {
  if (choice === 'auto') {
    localStorage.removeItem(KEY);
    document.documentElement.removeAttribute('data-theme');
  } else {
    localStorage.setItem(KEY, choice);
    document.documentElement.setAttribute('data-theme', choice);
  }
  window.dispatchEvent(new Event('bloomos-theme'));
}

export function toggleTheme(): void {
  setTheme(effectiveTheme() === 'command' ? 'field' : 'command');
}

export function useTheme(): 'command' | 'field' {
  const [t, setT] = useState(effectiveTheme);
  useEffect(() => {
    const on = () => setT(effectiveTheme());
    window.addEventListener('bloomos-theme', on);
    const mq = matchMedia('(prefers-color-scheme: dark)');
    mq.addEventListener('change', on);
    return () => { window.removeEventListener('bloomos-theme', on); mq.removeEventListener('change', on); };
  }, []);
  return t;
}
